/**
 * A person as one organization has them, in the record panel: their role,
 * which a platform admin may change; their groups, which decide what they
 * may read; their capability overrides; and removal from the organization.
 *
 * The person comes from the organization's user list the page already read,
 * so opening a row costs no request. The agent refuses to demote or remove
 * the last client admin; that refusal arrives as the error toast.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import {
  changeOrganizationUserRole,
  listOrganizationUsers,
  removeOrganizationUser,
} from '@optum/api-client/endpoints/organization-users';
import type { OrganizationRole, OrgMembership } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { type RecordRef, useRecordPanel } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordMissing,
  RecordOpen,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { Button } from '../../ui/Button.js';
import { useConfirm } from '../../ui/Confirm.js';
import { Avatar, Typography } from '../../ui/finixt.js';
import { SegmentedControl } from '../../ui/SegmentedControl.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { useToast } from '../../ui/useToast.js';
import { GroupSelection } from '../accounts/MemberGroupsEditor.js';
import { UserCapabilityConfigEditor } from '../accounts/UserCapabilityConfigEditor.js';
import { initialsOf, ROLE_LABEL } from './org-users.js';

const ROLE_ITEMS = (Object.keys(ROLE_LABEL) as OrganizationRole[]).map((value) => ({
  value,
  label: ROLE_LABEL[value],
}));

/** The org-user record: the person found in the tenant's list, or "not found". */
export function OrgUserRecord({ record }: { record: RecordRef }) {
  const orgId = record.orgId ?? '';
  const users = useQuery({
    queryKey: qk.orgUsers(orgId),
    queryFn: () => listOrganizationUsers(orgId),
  });
  return (
    <RecordAsync query={users} record={record}>
      {(rows) => {
        const user = rows.find((u) => u.user_id === record.id);
        return user ? <UserBody user={user} orgId={orgId} /> : <RecordMissing record={record} />;
      }}
    </RecordAsync>
  );
}

/** The error text an API refusal carries, or the thrown value as text. */
function reason(e: unknown): string {
  return e instanceof ApiError ? e.message : String(e);
}

function UserBody({ user, orgId }: { user: OrgMembership; orgId: string }) {
  const { isPlatformAdmin } = useAuth();
  const panel = useRecordPanel();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: qk.orgUsers(orgId) });
    // The platform's Accounts page counts memberships too.
    void qc.invalidateQueries({ queryKey: qk.accounts() });
  };
  const changeRole = useMutation({
    mutationFn: (role: OrganizationRole) => changeOrganizationUserRole(orgId, user.user_id, role),
    onSuccess: (updated) => {
      toast.success(`${updated.email} is now a ${ROLE_LABEL[updated.role].toLowerCase()}`);
      refresh();
    },
    onError: (e) => toast.error('Could not change the role', reason(e)),
  });
  const remove = useMutation({
    mutationFn: () => removeOrganizationUser(orgId, user.user_id),
    onSuccess: () => {
      toast.success(`Removed ${user.email} from the organization`);
      panel.close();
      refresh();
    },
    onError: (e) => toast.error('Could not remove the user', reason(e)),
  });

  async function confirmRemove(): Promise<void> {
    const ok = await confirm({
      title: 'Remove from this organization?',
      description: (
        <>
          <strong>{user.email}</strong> loses their membership here, and with it their groups and
          every document those grant. Their account and other organizations stay.
        </>
      ),
      confirmLabel: 'Remove',
      tone: 'danger',
    });
    if (ok) remove.mutate();
  }

  return (
    <RecordColumn>
      <RecordIdentity
        avatar={
          <Avatar
            initials={initialsOf(user.email)}
            size={40}
            shape="round"
            tint="var(--violet-tint)"
            color="var(--violet)"
          />
        }
        eyebrow={
          <>
            <Typography variant="eyebrow">User</Typography>
            <StatusBadge status={user.status} />
          </>
        }
        name={user.email}
        subtitle={ROLE_LABEL[user.role]}
      />
      <RecordSection label="Facts">
        <RecordFacts
          items={[
            { label: 'User', value: shortId(user.user_id) },
            { label: 'Subject', value: shortId(user.external_sub) },
            { label: 'Identity provider', value: user.idp_name, mono: false },
          ]}
        />
      </RecordSection>
      <RecordSection label="Role">
        {isPlatformAdmin ? (
          <SegmentedControl
            items={ROLE_ITEMS}
            value={user.role}
            disabledValues={changeRole.isPending ? ROLE_ITEMS.map((i) => i.value) : []}
            onChange={(next) => {
              if (next !== user.role) changeRole.mutate(next as OrganizationRole);
            }}
          />
        ) : (
          <span style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-body)' }}>
            {ROLE_LABEL[user.role]}
          </span>
        )}
      </RecordSection>
      {/* Changing either takes a platform admin, as on the account page. */}
      {isPlatformAdmin ? (
        <>
          <RecordSection label="Groups">
            <GroupSelection orgId={orgId} platformUserId={user.user_id} />
          </RecordSection>
          <RecordSection label="Capability overrides">
            <UserCapabilityConfigEditor
              platformUserId={user.user_id}
              organizationId={orgId}
              memberships={[
                {
                  organization_id: orgId,
                  organization_name: '',
                  role: user.role,
                  status: user.status,
                },
              ]}
            />
          </RecordSection>
        </>
      ) : null}
      <RecordActions>
        <RecordOpen
          to={`/accounts/${encodeURIComponent(user.external_sub)}`}
          label="Open account page"
        />
        {isPlatformAdmin ? (
          <Button
            variant="danger"
            size="sm"
            disabled={remove.isPending}
            onClick={() => void confirmRemove()}
          >
            {remove.isPending ? 'Removing…' : 'Remove from organization'}
          </Button>
        ) : null}
      </RecordActions>
    </RecordColumn>
  );
}
